import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Button, Form, Spinner } from 'react-bootstrap';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const PasswordResetForm = ({ hasLabel }) => {
  const [tokenData, setTokenData] = useState({});
  const [userData, setUserData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const passwordResetSchema = Yup.object().shape({
    password: Yup.string()
      .required(t('newPasswordRequiredText'))
      .matches(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/, t('passwordCriteriaText')),
    confirmPassword: Yup.string()
      .required(t('confirmPasswordRequiredText'))
      .oneOf([Yup.ref('password'), null], t('newPasswordAndConfirmPasswordSameText')),
  });

  const methods = useForm({
    mode: 'onChange', // Validates on each input change
    resolver: yupResolver(passwordResetSchema), // Integrate Yup validation
    defaultValues: {
      password: '', // Default value for password
      confirmPassword: '', // Default value for confirmPassword
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
    trigger
  } = methods;

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const fieldsWithErrors = Object.keys(errors);
      trigger(fieldsWithErrors);
    }
  }, [i18n.language]);

  // Extract token from the URL query string (assuming the token is passed as a query parameter)
  const getTokenFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('token'); // Replace 'token' with the actual query parameter name
  };

  const hashPassword = async (password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Fetch token data from the backend and validate it
  const fetchTokenData = async () => {
    try {
      const token = getTokenFromUrl();
      if (!token) {
        console.warn("No token found in URL");
        navigate("/authentication/login");
        return null;
      }

      const tokenResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/tokenDataRoute/getTokenDataByToken/${token}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      const tokenData = tokenResponse.data;

      // Redirect if TokenUsed is true
      if (tokenData.TokenUsed) {
        toast.warn(t("emailLinkUsedToast"), { theme: "colored" });
        navigate("/authentication/login");
        return null; // Stop further processing
      }
      setTokenData(tokenData); // Update state with token data
      return tokenData;
    } catch (error) {
      console.error("Error fetching token data:", error);
      toast.error(t("tokenFetchErrorText"), { theme: "colored" });
      navigate("/authentication/login");
      return null;
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      const tokenData = await fetchTokenData(); // Fetch and validate token data
      if (!tokenData) return; // Stop execution if token validation fails

      // Fetch user data if TokenData is valid
      if (tokenData.UserId) {
        try {
          const userResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${tokenData.UserId}`, {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
          );
          setUserData(userResponse.data);
        } catch (error) {
          console.error("Error fetching user data:", error);
          toast.error(t("userFetchErrorText"), { theme: "colored" });
        }
      }
      setIsLoading(false);
    };

    initialize();
  }, [location.search, navigate]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);

      const updatedTokenData = await fetchTokenData();
      if (!updatedTokenData) return;

      const hashedPassword = await hashPassword(data.password);
      const userId = updatedTokenData.UserId;
      const requestData = { userId, newPassword: hashedPassword };

      //  Reset password in backend
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/resetPassword`,
        requestData,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      //  Mark the token as used in the backend
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/tokenDataRoute/updateTokenData/${updatedTokenData.Token}`,
        { tokenUsed: true },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      //  Show success toast and redirect
      toast.success(t("passwordResetSuccessToast"), { theme: "colored" });
      navigate("/authentication/login");

    } catch (error) {
      console.error("Error during password reset:", error);
      toast.error(t("passwordResetErrorText"), { theme: "colored" });
    } finally {
      setIsLoading(false);
      reset();
    }
  };

  return (
    <FormProvider {...methods}>
      {isLoading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </Spinner>
        </div>
      ) : (
        <Form
          className={classNames('mt-3', { 'text-left': hasLabel })}
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* New Password */}
          <Form.Group className="mb-3">
            {hasLabel && <Form.Label>{t('newPasswordText')}</Form.Label>}
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="password"
                  placeholder={!hasLabel ? t('newPasswordText') : ''}
                  isInvalid={!!errors.password}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.password?.message}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Confirm Password */}
          <Form.Group className="mb-3">
            {hasLabel && <Form.Label>{t('confirmPasswordText')}</Form.Label>}
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="password"
                  placeholder={!hasLabel ? t('confirmPasswordText') : ''}
                  isInvalid={!!errors.confirmPassword}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.confirmPassword?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Button type="submit" className="w-100">
            {t('setPasswordText')}
          </Button>
        </Form>
      )}
    </FormProvider>
  );
};

PasswordResetForm.propTypes = {
  hasLabel: PropTypes.bool,
};

export default PasswordResetForm;